import { AppConfig } from '@dh-io-emh/config-types';
import { infuse } from '../ioc-config';
import Cart from '../models/cart.model';
import { createHttpService } from '../utils/http.util';
import {
  DCPGuestLoginDTO,
  DCPPaymentAssetsDisplayDTO,
  DCPPaymentModeAssetDTO,
  DCPPaymentModeDisplayDTO,
  DCPVendorInformationsDTO,
  DCPWheelSetOrderInfoDTO,
  SMSAddress,
  SMSCart,
  SMSDeliveryMode,
  SMSDeliveryModeList,
  SMSOrderEntry,
  SMSPaymentModeDTO,
} from '@dh-io-emh/commerce-api/src/models/shared/ocb/api.schema';

export type IUpdateCartEntryPayload = {
  deliveryPointOfService: {
    name: string;
  };
  wheelSetOrderInfo?: DCPWheelSetOrderInfoDTO & {
    name?: string;
    subtitle?: string;
    wheels: any[];
  };
};

export type CartApi = ReturnType<typeof createCartApi>;

type PostCartPayload = {
  cartId?: string;
  quoteId?: string;
  userContext?: 'B2C' | 'B2B';
  customerGroupCode?: string;
  customerPostalCode?: string | null;
  customerProfileId?: string | null;
  customerGroupId?: string | null;
  companyMeId?: string | null;
  // TODO: only temporary, remove after b2x is globally enabled
  b2xEnabled?: boolean;
};

export const createCartApi = ({ config: { apiBaseUrl, lang, siteId } }: { config: AppConfig }) => {
  const http = createHttpService({
    url: `${apiBaseUrl}/v2/${siteId}`,
    params: { lang },
  });

  return {
    async getCart(userId: string, cartId: string) {
      let url = `users/${userId}/carts`;

      if (cartId) {
        url += `/${cartId}`;
      }

      const { data } = await http.get<SMSCart>(url, {
        params: {
          fields: 'DCP',
        },
      });

      return Cart.create(data);
    },

    async postCart(userId: string, payload: PostCartPayload) {
      let url = `users/${userId}/carts`;

      if (payload.cartId) {
        url += `/${payload.cartId}`;
      }

      const { data } = await http.post<SMSCart>(url, {
        params: {
          fields: 'DCP',
          quoteId: payload.quoteId,
        },
        // TODO: remove b2xEnabled condition after b2x is globally enabled
        ...(payload.b2xEnabled && {
          data: {
            dcpCustomerClassification: {
              businessModel: payload.userContext,
              ...(payload.customerProfileId && { customerProfileId: payload.customerProfileId }),
              ...(payload.customerGroupId && { customerGroupId: payload.customerGroupId }),
              ...(payload.customerGroupCode && { customerGroupCode: payload.customerGroupCode }),
              ...(payload.customerPostalCode && { customerPostalCode: payload.customerPostalCode }),
              ...(payload.companyMeId && { companyMeId: payload.companyMeId }),
            },
          },
        }),
        withCredentials: true,
      });

      return Cart.create(data);
    },

    async updateCart(userId: string, cartId: string, requestBody: Partial<SMSCart>) {
      const { shopCountry } = infuse('config');
      const headers = {
        insuranceFlag: 'true',
      };
      const params = {
        fields: 'DCP',
      };
      const { data } = await http.patch<SMSCart>(`users/${userId}/carts/${cartId}`, {
        data: requestBody,
        params,
        ...(shopCountry === 'TR' && requestBody.downstreamPaymentMode === 'DIRECT_PAYMENT' && { headers }),
      });

      return Cart.create(data);
    },

    async postCartEntry(
      userId: string,
      cartId: string,
      productCode: string,
      deliveryPointOfService: string,
      quantity: number,
      sourceProductCode?: string,
    ) {
      const { data } = await http.post<SMSCart>(`users/${userId}/carts/${cartId}/entries`, {
        data: {
          product: {
            code: productCode,
          },
          deliveryPointOfService: {
            name: deliveryPointOfService,
          },
          quantity,
          ...(sourceProductCode && { sourceProductCode: sourceProductCode }),
        },
      });

      return Cart.create(data);
    },

    async updateCartEntry(userId: string, cartId: string, entryNumber: number, requestBody: IUpdateCartEntryPayload) {
      const { data } = await http.patch<SMSOrderEntry>(`users/${userId}/carts/${cartId}/entries/${entryNumber}`, {
        data: requestBody,
      });

      return data;
    },

    /**
     * Makes the backend to transform the old anonymous cart into a user cart
     * for the authenticated user.
     *
     * @param {number} userId
     * @param {number} oldCartGuid
     * @returns {Promise} resolving the new cart
     */
    async transformAnonymousCart(oldCartGuid) {
      const { data } = await http.post<SMSCart>(`users/current/carts`, {
        params: {
          oldCartId: oldCartGuid,
          fields: 'DCP',
        },
      });

      return data ? Cart.create(data) : [];
    },

    async setEmailForAnonymousCart(cartId, email): Promise<DCPGuestLoginDTO> {
      const { data } = await http.put<DCPGuestLoginDTO>(`users/anonymous/carts/${cartId}/email`, {
        data: {
          email,
        },
      });

      return data;
    },

    async postCartPaymentAddress(userId: string, cartId: string, newPaymentAddress: SMSAddress): Promise<SMSAddress> {
      const { data } = await http.post<SMSAddress>(`users/${userId}/carts/${cartId}/addresses/payment`, {
        params: {
          fields: 'DCP',
        },
        data: newPaymentAddress,
      });

      return data;
    },

    async putCartPaymentAddress(userId: string, cartId: string, modifiedPaymentAddress: SMSAddress): Promise<void> {
      await http.put<SMSAddress>(`users/${userId}/carts/${cartId}/addresses/payment`, {
        params: {
          fields: 'DCP',
        },
        data: { id: modifiedPaymentAddress.id },
      });
    },

    async postCartContactAddress(userId: string, cartId: string, newContactAddress: SMSAddress): Promise<SMSAddress> {
      const { data } = await http.post<SMSAddress>(`users/${userId}/carts/${cartId}/addresses/contact`, {
        params: {
          fields: 'DCP',
        },
        data: newContactAddress,
      });

      return data;
    },

    async putCartContactAddress(userId: string, cartId: string, modifiedContactAddress: SMSAddress): Promise<void> {
      await http.put<SMSAddress>(`users/${userId}/carts/${cartId}/addresses/contact`, {
        params: {
          fields: 'DCP',
        },
        data: { id: modifiedContactAddress.id },
      });
    },

    async getDeliveryModes(userId: string, cartId: string): Promise<SMSDeliveryMode[]> {
      const url = `users/${userId}/carts/${cartId}/deliverymodes`;

      const { data } = await http.get<SMSDeliveryModeList>(url, {
        params: {
          fields: 'DCP',
        },
      });

      return data.deliveryModes || [];
    },

    async setDeliveryMode(userId: string, cartId: string, deliveryModeCode: string): Promise<void> {
      const url = `users/${userId}/carts/${cartId}/deliverymode`;

      await http.put<SMSDeliveryMode>(url, {
        params: {
          fields: 'DCP',
        },
        data: {
          code: deliveryModeCode,
        },
      });
    },

    async getPaymentModes(userId: string, cartId: string): Promise<DCPPaymentModeDisplayDTO[]> {
      const url = `users/${userId}/carts/${cartId}/paymentmodes`;

      const { data } = await http.get<{ items: DCPPaymentModeDisplayDTO[] }>(url, {
        params: {
          fields: 'DCP',
        },
      });

      return data.items || [];
    },

    async putCartPaymentMode(userId: string, cartId: string, paymentMode: SMSPaymentModeDTO): Promise<void> {
      const url = `users/${userId}/carts/${cartId}/paymentmode`;

      await http.put<string>(url, {
        data: paymentMode,
      });
    },

    getPaymentAssets: async (userId: string, cartId: string): Promise<DCPPaymentAssetsDisplayDTO> => {
      const { data } = await http.get<DCPPaymentAssetsDisplayDTO>(`users/${userId}/carts/${cartId}/paymentassets`);

      return data;
    },

    /**
     * Function to temporarily add a newly added MPay asset to the current checkout process.
     */
    postInterimAsset: (userId: string, cartId: string, asset) => {
      const url = `users/${userId}/carts/${cartId}/interimassets`;

      return http.post(url, { data: asset });
    },

    async getVendorsInfo(userId: string, cartId: string): Promise<DCPVendorInformationsDTO> {
      const url = `users/${userId}/carts/${cartId}/vendorsinfo`;

      const { data } = await http.get<DCPVendorInformationsDTO>(url, {
        params: {
          fields: 'DCP',
        },
      });

      return data;
    },

    // creates a new address and assign it to the cart
    postDeliveryAddress(userId: string, cartId: string, deliveryAddress: SMSAddress) {
      return http.post(`users/${userId}/carts/${cartId}/addresses/delivery`, {
        data: deliveryAddress,
      });
    },

    // uses an pre-existing address and assign it to the cart
    updateDeliveryAddress(userId: string, cartId: string, addressId: string) {
      return http.put(`users/${userId}/carts/${cartId}/addresses/delivery`, {
        data: { id: addressId },
      });
    },
  };
};
